import React, { useEffect, useMemo, useState } from "react";
import Table from "../../../components/reusable-table/Table";
import "./Questions.css";
import { DeleteIcon, EditIcon, SearchIcon } from "../../../icons/reactIcons";
import Input from "../../../components/reusable-input/Input";
import ViewQuestionModal from "./modals/ViewQuestionModal";
import EditQuestionModal from "./modals/EditQuestionModal";
import { useAppDispatch, useAppSelector } from "../../../redux/store/hooks";
import {
  fetchQuestionList,
  createQuestionThunk,
  updateQuestionThunk,
  deleteQuestionThunk,
} from "../../../redux/slices/questionSlice";
import ConfirmUserActionModal from "../../../components/confirm-modal/ConfirmUserActionModal";
import Loader from "../../../components/custom-loader/Loader";
import { formatDateTime } from "../../../utils/formatDateTime";

/* ---------- Table Columns ---------- */

const columns = [
  { header: "Question", accessor: "question" },
  { header: "Created By", accessor: "createdBy" },
  { header: "Updated By", accessor: "updatedBy" },
];

const Questions: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    list: questions,
    loading,
    submitting,
  } = useAppSelector((state) => state.questions);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedQuestion, setSelectedQuestion] = useState<any | null>(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  /* ---------- Fetch Data ---------- */

  useEffect(() => {
    if (!questions.length) {
      dispatch(fetchQuestionList());
    }

  }, [dispatch, questions.length]);

  /* ---------- Table Actions ---------- */

  const actions = [
    {
      label: <DeleteIcon />,
      onClick: (row: any) => {
        setSelectedQuestion(row.original);
        setOpenDeleteModal(true);
      },
    },
    {
      label: <EditIcon />,
      onClick: (row: any) => {
        setSelectedQuestion(row.original);
        setCreateMode(false);
        setEditOpen(true);
      },
    },
  ];

  /* ---------- Table Data ---------- */

  const tableData = useMemo(() => {
    return questions
      .filter((q) =>
        q.questionName.toLowerCase().includes(searchTerm.toLowerCase()),
      )
      .map((q) => ({
        question: q.questionName,
        original: q,

        createdBy: (
          <div className="merged-cell">
            <div className="merged-label">{q.creator?.name || "_"}</div>
            <div className="merged-date">{formatDateTime(q.createdAt)}</div>
          </div>
        ),

        updatedBy: (
          <div className="merged-cell">
            {!q.updater?.name ? (
              "_"
            ) : (
              <>
                <div className="merged-label">{q.updater?.name || "_"}</div>
                <div className="merged-date">{formatDateTime(q.updatedAt)}</div>
              </>
            )}
          </div>
        ),
      }));
  }, [questions, searchTerm]);

  /* ---------- Delete (Soft Delete) ---------- */

  const handleDeleteQuestion = async () => {
    if (!selectedQuestion) return;

    try {
      const res = await dispatch(
        deleteQuestionThunk(selectedQuestion),
      ).unwrap();

      if (res.status) {
        setOpenDeleteModal(false);
        setSelectedQuestion(null);
      }
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  if (loading)
    return (
      <>
        <Loader />
      </>
    );

  return (
    <div className="questions-container">
      {/* ---------- Header ---------- */}
      <div className="questions-header">
        <h2>Questions</h2>

        <div className="questions-actions">
          <Input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            search
            startIcon={<SearchIcon fontSize="20px" />}
            removeBottomMargin
          />

          <button
            className="user-groups-btn"
            onClick={() => {
              setSelectedQuestion(null);
              setCreateMode(true);
              setEditOpen(true);
            }}
          >
            Create New
          </button>
        </div>
      </div>

      {/* ---------- Table ---------- */}
      <Table
        columns={columns}
        data={tableData}
        actions={actions}
        rowsPerPage={15}
        rounded
        // loading={loading}
        onRowClick={(row) => {
          setSelectedQuestion(row.original);
          setViewOpen(true);
        }}
      />

      {/* View Modal */}
      <ViewQuestionModal
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        onEdit={() => {
          setViewOpen(false);
          setEditOpen(true);
          setCreateMode(false);
        }}
        onDelete={() => {
          setViewOpen(false);
          setOpenDeleteModal(true);
        }}
        question={selectedQuestion}
      />

      {/* ---------- Edit / Create Modal ---------- */}
      <EditQuestionModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        submitting={submitting}
        initialData={createMode ? undefined : selectedQuestion}
        onSubmit={async (payload) => {
          try {
            if (createMode) {
              const res = await dispatch(createQuestionThunk(payload)).unwrap();

              if (res.status) setEditOpen(false);
            } else if (selectedQuestion) {
              const res = await dispatch(
                updateQuestionThunk({
                  id: selectedQuestion.id,
                  payload: { ...payload, isActive: true },
                }),
              ).unwrap();

              if (res.status) setEditOpen(false);
            }
          } catch (err) {
            console.error(err);
          }
        }}
      />

      {/* ---------- Delete Confirmation ---------- */}
      {openDeleteModal && (
        <ConfirmUserActionModal
          type="delete"
          userName="this question"
          loading={submitting}
          onClose={() => setOpenDeleteModal(false)}
          onConfirm={handleDeleteQuestion}
        />
      )}
    </div>
  );
};

export default Questions;
